/* eslint react/no-array-index-key: 0 */

import { forbidExtraProps, mutuallyExclusiveProps, nonNegativeInteger } from 'airbnb-prop-types';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';

import { CalendarDayPhrases } from '../defaultPhrases';
import getPhrasePropTypes from '../utils/getPhrasePropTypes';

import CalendarDay from './CalendarDay';
import CalendarWeek from './CalendarWeek';

import calculateDimension from '../utils/calculateDimension';
import getCalendarMonthWeeks from '../utils/getCalendarMonthWeeks';
import isSameDay from '../utils/isSameDay';
import toISODateString from '../utils/toISODateString';

import DayOfWeekShape from '../shapes/DayOfWeekShape';
import ModifiersShape from '../shapes/ModifiersShape';
import ScrollableOrientationShape from '../shapes/ScrollableOrientationShape';

import { DAY_SIZE, HORIZONTAL_ORIENTATION, VERTICAL_SCROLLABLE } from '../constants';
import getFirstDayOfWeek from '../utils/getFirstDayOfWeek';
import mergeClassNames from '../utils/mergeClassNames';
import mergeStyles from '../utils/mergeStyles';

const propTypes = forbidExtraProps({
    month: PropTypes.instanceOf(DateTime),
    horizontalMonthPadding: nonNegativeInteger,
    isVisible: PropTypes.bool,
    enableOutsideDays: PropTypes.bool,
    modifiers: PropTypes.objectOf(ModifiersShape),
    orientation: ScrollableOrientationShape,
    daySize: nonNegativeInteger,
    onDayClick: PropTypes.func,
    onDayMouseEnter: PropTypes.func,
    onDayMouseLeave: PropTypes.func,
    onMonthSelect: PropTypes.func,
    onYearSelect: PropTypes.func,
    renderMonthText: mutuallyExclusiveProps(PropTypes.func, 'renderMonthText', 'renderMonthElement'),
    renderCalendarDay: PropTypes.func,
    renderDayContents: PropTypes.func,
    renderMonthElement: mutuallyExclusiveProps(PropTypes.func, 'renderMonthText', 'renderMonthElement'),
    firstDayOfWeek: DayOfWeekShape,
    setMonthTitleHeight: PropTypes.func,
    verticalBorderSpacing: nonNegativeInteger,

    focusedDate: PropTypes.instanceOf(DateTime), // indicates focusable day
    isFocused: PropTypes.bool, // indicates whether or not to move focus to focusable day

    // i18n
    monthFormat: PropTypes.string,
    phrases: PropTypes.shape(getPhrasePropTypes(CalendarDayPhrases)),
    dayAriaLabelFormat: PropTypes.string,
});

const defaultProps = {
    month: DateTime.now(),
    horizontalMonthPadding: 13,
    isVisible: true,
    enableOutsideDays: false,
    modifiers: {},
    orientation: HORIZONTAL_ORIENTATION,
    daySize: DAY_SIZE,
    onDayClick() {},
    onDayMouseEnter() {},
    onDayMouseLeave() {},
    onMonthSelect() {},
    onYearSelect() {},
    renderMonthText: null,
    renderCalendarDay: (props) => <CalendarDay {...props} />,
    renderDayContents: null,
    renderMonthElement: null,
    firstDayOfWeek: null,
    setMonthTitleHeight: null,

    focusedDate: null,
    isFocused: false,

    // i18n
    monthFormat: 'MMMM yyyy', // english locale
    phrases: CalendarDayPhrases,
    dayAriaLabelFormat: undefined,
    verticalBorderSpacing: undefined,
};

class CalendarMonth extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            weeks: getCalendarMonthWeeks(
                props.month,
                props.enableOutsideDays,
                props.firstDayOfWeek == null
                    ? getFirstDayOfWeek() // Get first day of week based on locale or default
                    : props.firstDayOfWeek
            ),
        };

        this.setCaptionRef = this.setCaptionRef.bind(this);
        this.setMonthTitleHeight = this.setMonthTitleHeight.bind(this);
    }

    componentDidMount() {
        this.queueSetMonthTitleHeight();
    }

    componentWillReceiveProps(nextProps) {
        const { month, enableOutsideDays, firstDayOfWeek } = nextProps;
        const {
            month: prevMonth,
            enableOutsideDays: prevEnableOutsideDays,
            firstDayOfWeek: prevFirstDayOfWeek,
        } = this.props;
        if (
            !month.equals(prevMonth) ||
            enableOutsideDays !== prevEnableOutsideDays ||
            firstDayOfWeek !== prevFirstDayOfWeek
        ) {
            this.setState({
                weeks: getCalendarMonthWeeks(
                    month,
                    enableOutsideDays,
                    firstDayOfWeek == null ? getFirstDayOfWeek() : firstDayOfWeek
                ),
            });
        }
    }

    componentDidUpdate(prevProps) {
        const { setMonthTitleHeight } = this.props;

        if (prevProps.setMonthTitleHeight === null && setMonthTitleHeight !== null) {
            this.queueSetMonthTitleHeight();
        }
    }

    componentWillUnmount() {
        if (this.setMonthTitleHeightTimeout) {
            clearTimeout(this.setMonthTitleHeightTimeout);
        }
    }

    setMonthTitleHeight() {
        const { setMonthTitleHeight } = this.props;
        if (setMonthTitleHeight) {
            const captionHeight = calculateDimension(this.captionRef, 'height', true, true);
            setMonthTitleHeight(captionHeight);
        }
    }

    setCaptionRef(ref) {
        this.captionRef = ref;
    }

    queueSetMonthTitleHeight() {
        this.setMonthTitleHeightTimeout = window.setTimeout(this.setMonthTitleHeight, 0);
    }

    render() {
        const {
            dayAriaLabelFormat,
            daySize,
            focusedDate,
            horizontalMonthPadding,
            isFocused,
            isVisible,
            modifiers,
            month,
            monthFormat,
            onDayClick,
            onDayMouseEnter,
            onDayMouseLeave,
            onMonthSelect,
            onYearSelect,
            orientation,
            phrases,
            renderCalendarDay,
            renderDayContents,
            renderMonthElement,
            renderMonthText,

            verticalBorderSpacing,
        } = this.props;

        const { weeks } = this.state;
        const monthTitle = renderMonthText ? renderMonthText(month) : month.toFormat(monthFormat);

        const verticalScrollable = orientation === VERTICAL_SCROLLABLE;

        return (
            <div
                style={mergeStyles({ padding: `0 ${horizontalMonthPadding}px` })}
                className={mergeClassNames('CalendarMonth')}
                data-visible={isVisible}
            >
                <div
                    ref={this.setCaptionRef}
                    className={mergeClassNames(
                        'CalendarMonth_caption',
                        verticalScrollable && 'CalendarMonth_caption__verticalScrollable'
                    )}
                >
                    {renderMonthElement ? (
                        renderMonthElement({
                            month,
                            onMonthSelect,
                            onYearSelect,
                            isVisible,
                        })
                    ) : (
                        <strong>{monthTitle}</strong>
                    )}
                </div>

                <table
                    style={mergeStyles(verticalBorderSpacing && { borderSpacing: `0px ${verticalBorderSpacing}px` })}
                    className={mergeClassNames(
                        !verticalBorderSpacing && 'CalendarMonth_table',
                        verticalBorderSpacing && 'CalendarMonth_verticalSpacing'
                    )}
                    role="presentation"
                >
                    <tbody>
                        {weeks.map((week, i) => {
                            return (
                                <CalendarWeek key={i}>
                                    {week.map((day, dayOfWeek) => {
                                        return renderCalendarDay({
                                            key: dayOfWeek,
                                            day,
                                            daySize,
                                            isOutsideDay: !day || day.month !== month.month,
                                            tabIndex: isVisible && isSameDay(day, focusedDate) ? 0 : -1,
                                            isFocused,
                                            onDayMouseEnter,
                                            onDayMouseLeave,
                                            onDayClick,
                                            renderDayContents,
                                            phrases,
                                            modifiers: modifiers[toISODateString(day)],
                                            ariaLabelFormat: dayAriaLabelFormat,
                                        });
                                    })}
                                </CalendarWeek>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }
}

CalendarMonth.propTypes = propTypes;
CalendarMonth.defaultProps = defaultProps;

export { CalendarMonth as PureCalendarMonth };
export default CalendarMonth;
